import { ActionIcon, Button, Group, Modal, Paper, Stack, Switch, Text, Title, Tooltip } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconEye, IconEyeOff } from '@tabler/icons-react';
import { useState } from 'react';
import { barve } from '../tema';
import { DEJANJA_DELAVNICE, DEJANJA_PRODAJE, HITRA_DEJANJA } from './hitraDejanja';
import { KARTICE_ZACETNE, PRIVZETA_DEJANJA, premakniMed, type HitroDejanje, type Nastavitve } from './zacetna';

/**
 * Okno »Prilagodi začetno stran« – vse osebne nastavitve začetnega zaslona na enem mestu: hitra dejanja (puščici za
 * vrstni red in oko za skrivanje ob vsakem; skrito ostane v seznamu prečrtano) in pregled poslovanja (stikala za
 * številke z grafom in za vsako kartico). »Privzeto« vrne vse na začetno stanje; shrani se šele s »Shrani«, velja za
 * uporabnika na vseh napravah. Številke lahko skrije tudi bližnjica »Skrij številke« na zaslonu. Dejanj modulov, ki jih
 * paket nima, ni na seznamu (premikanje jih preskoči), brez modula Prodaja tudi ne pregleda poslovanja.
 */
export function PrilagoditevZacetne({
  nastavitve,
  prodaja,
  delavnica,
  odprto,
  zapri,
  shrani,
}: {
  nastavitve: Nastavitve;
  prodaja: boolean;
  delavnica: boolean;
  odprto: boolean;
  zapri: () => void;
  shrani: (n: Nastavitve) => void;
}) {
  const vPaketu = (k: HitroDejanje) =>
    (prodaja || !DEJANJA_PRODAJE.includes(k)) && (delavnica || !DEJANJA_DELAVNICE.includes(k));
  return (
    <Modal opened={odprto} onClose={zapri} title="Prilagodi začetno stran" styles={{ title: { fontWeight: 700 } }}>
      {/* vsebina se ob vsakem odprtju na novo postavi iz shranjenih nastavitev */}
      {odprto && <Vsebina zacetne={nastavitve} vPaketu={vPaketu} prodaja={prodaja} zapri={zapri} shrani={shrani} />}
    </Modal>
  );
}

const PRIVZETO: Nastavitve = {
  skrijPregled: false,
  hitraDejanja: PRIVZETA_DEJANJA,
  skriteKartice: [],
  skritaDejanja: [],
};

function Vsebina({
  zacetne,
  vPaketu,
  prodaja,
  zapri,
  shrani,
}: {
  zacetne: Nastavitve;
  vPaketu: (k: HitroDejanje) => boolean;
  prodaja: boolean;
  zapri: () => void;
  shrani: (n: Nastavitve) => void;
}) {
  const [n, setN] = useState(zacetne);
  const red = n.hitraDejanja.filter(vPaketu);
  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={3} size="h5">
          Hitra dejanja
        </Title>
        <Text size="sm" c={barve.besediloSekundarno}>
          S puščicama premaknite dejanja, ki jih uporabljate največ, na začetek vrstice. Z očesom skrijete gumb, ki ga
          ne potrebujete.
        </Text>
        <Stack gap={6} role="list" aria-label="Hitra dejanja">
          {red.map((k, i) => {
            const { naziv, Ikona } = HITRA_DEJANJA[k];
            const skrito = n.skritaDejanja.includes(k);
            return (
              <Paper key={k} withBorder radius="md" px="sm" py={4} role="listitem" aria-label={naziv}>
                <Group gap={4} wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0, opacity: skrito ? 0.45 : 1 }}>
                    <Ikona size={20} stroke={1.8} color={barve.primarna} style={{ flexShrink: 0 }} />
                    <Text fw={600} truncate td={skrito ? 'line-through' : undefined}>
                      {naziv}
                    </Text>
                  </Group>
                  <Tooltip label={skrito ? 'Prikaži gumb' : 'Skrij gumb'}>
                    <ActionIcon
                      variant="subtle"
                      color={skrito ? 'gray' : undefined}
                      size={40}
                      aria-label={`${skrito ? 'Prikaži' : 'Skrij'} ${naziv}`}
                      aria-pressed={skrito}
                      onClick={() =>
                        setN((x) => ({
                          ...x,
                          skritaDejanja: skrito ? x.skritaDejanja.filter((d) => d !== k) : [...x.skritaDejanja, k],
                        }))
                      }
                    >
                      {skrito ? <IconEyeOff size={20} /> : <IconEye size={20} />}
                    </ActionIcon>
                  </Tooltip>
                  <ActionIcon
                    variant="subtle"
                    size={40}
                    disabled={i === 0}
                    aria-label={`Premakni ${naziv} gor`}
                    onClick={() => setN((x) => ({ ...x, hitraDejanja: premakniMed(x.hitraDejanja, k, -1, vPaketu) }))}
                  >
                    <IconArrowUp size={20} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size={40}
                    disabled={i === red.length - 1}
                    aria-label={`Premakni ${naziv} dol`}
                    onClick={() => setN((x) => ({ ...x, hitraDejanja: premakniMed(x.hitraDejanja, k, 1, vPaketu) }))}
                  >
                    <IconArrowDown size={20} />
                  </ActionIcon>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      </Stack>

      {prodaja && (
        <Stack gap="sm">
          <Title order={3} size="h5">
            Pregled poslovanja
          </Title>
          <Switch
            label="Številke in graf"
            description="Prodaja, zaslužek, zaloga in povpraševanja tega meseca ter graf zadnjih 12 mesecev"
            checked={!n.skrijPregled}
            onChange={(e) => {
              const prikazi = e.currentTarget.checked;
              setN((x) => ({ ...x, skrijPregled: !prikazi }));
            }}
          />
          {KARTICE_ZACETNE.map(({ k, naslov }) => (
            <Switch
              key={k}
              label={naslov}
              checked={!n.skriteKartice.includes(k)}
              onChange={(e) => {
                const prikazi = e.currentTarget.checked;
                setN((x) => ({
                  ...x,
                  skriteKartice: prikazi ? x.skriteKartice.filter((d) => d !== k) : [...x.skriteKartice, k],
                }));
              }}
            />
          ))}
          <Text size="xs" c={barve.besediloSekundarno}>
            Skrita kartica Povpraševanja ali Zanimanje kupcev se vseeno pokaže, kadar je v njej neogledano
            povpraševanje.
          </Text>
        </Stack>
      )}

      <Group justify="space-between" gap="sm">
        <Button variant="subtle" onClick={() => setN(PRIVZETO)}>
          Privzeto
        </Button>
        <Group gap="sm">
          <Button variant="default" onClick={zapri}>
            Prekliči
          </Button>
          <Button
            onClick={() => {
              shrani(n);
              zapri();
            }}
          >
            Shrani
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}
